import React from 'react';
import { View as RNView, StyleSheet, Text, TouchableOpacity } from 'react-native';
import { CalendarDate, RangeSelection } from '@shared/components/dateAndTime/calendar/index';
import { MBTypographyStylesRaw } from '@shared/components/typography';
import { Colors } from '@shared/styles';

const CELL_SIZE = 40;
const SELECTED_SIZE = 33;

interface Props {
  day: CalendarDate;
  disabled: boolean;
  onPress(): void;
}

export function MBCalendarDay({ day, disabled, onPress }: Props) {
  if (!day) {
    return <RNView style={styles.cell} />;
  }

  const { label, selected, weekend, today, range, highlighted, currentMonth } = day;
  const isRangeStart = range === RangeSelection.start || range === RangeSelection.single;
  const isRangeEnd = range === RangeSelection.end || range === RangeSelection.single;
  const isRangeBoundary = isRangeStart || isRangeEnd;
  const isSelected = selected || isRangeBoundary;
  return (
    <TouchableOpacity
      disabled={disabled}
      style={[styles.cell, disabled && styles.cellDisabled]}
      onPress={onPress}
    >
      {range === RangeSelection.start && <RNView style={styles.rangeStartFill} />}
      {range === RangeSelection.middle && <RNView style={styles.rangeMiddleFill} />}
      {range === RangeSelection.end && <RNView style={styles.rangeEndFill} />}
      <RNView
        style={[
          highlighted && styles.dayHighlighted,
          isSelected && styles.daySelected,
          today && isSelected && styles.daySelectedToday,
        ]}
        testID={today ? 'calendar-picker-today' : undefined}
      >
        <Text
          style={[
            styles.dayText,
            weekend && styles.dayTextWeekend,
            !currentMonth && styles.dayTextOutsideMonth,
            today && styles.dayTextToday,
            disabled && styles.dayTextDisabled,
            highlighted && styles.dayTextHighlighted,
            isSelected && styles.dayTextSelected,
          ]}
        >
          {label}
        </Text>
      </RNView>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  cell: {
    flex: 1,
    height: CELL_SIZE,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cellDisabled: {
    opacity: 0.3,
  },
  daySelected: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.v2.purple,
    borderRadius: SELECTED_SIZE / 2,
    maxWidth: SELECTED_SIZE,
    minWidth: SELECTED_SIZE,
    minHeight: SELECTED_SIZE,
    maxHeight: SELECTED_SIZE,
  },
  dayHighlighted: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.v2.green10,
    borderRadius: SELECTED_SIZE / 2,
    maxWidth: SELECTED_SIZE,
    minWidth: SELECTED_SIZE,
    minHeight: SELECTED_SIZE,
    maxHeight: SELECTED_SIZE,
  },
  daySelectedToday: {
    backgroundColor: Colors.v2.green,
  },
  dayText: {
    ...MBTypographyStylesRaw.default,
    ...MBTypographyStylesRaw.subtitle,
    color: Colors.v2.purple,
  },
  dayTextSelected: {
    color: Colors.v2.white,
    fontSize: 16,
  },
  dayTextToday: {
    color: Colors.v2.green,
  },
  dayTextDisabled: {
    color: Colors.v2.gray50,
  },
  dayTextWeekend: {
    color: Colors.v2.gray50,
  },
  dayTextOutsideMonth: {
    color: Colors.v2.gray50,
    fontSize: 15,
  },
  dayTextHighlighted: {
    color: Colors.v2.purple,
    fontSize: 16,
  },
  rangeStartFill: {
    backgroundColor: Colors.v2.gray05,
    height: SELECTED_SIZE,
    width: '50%',
    position: 'absolute',
    right: 0,
  },
  rangeMiddleFill: {
    backgroundColor: Colors.v2.gray05,
    height: SELECTED_SIZE,
    width: '100%',
    position: 'absolute',
  },
  rangeEndFill: {
    backgroundColor: Colors.v2.gray05,
    height: SELECTED_SIZE,
    width: '50%',
    position: 'absolute',
    left: 0,
  },
});
